import { usercentricsConsentChange, trackCookieBannerInteraction } from './usercentrics';
import { useCookie } from './cookies';

// Utility functions for GTM data layer initialization
export const getUserInfo = () => {
  // Get user ID from cookie (similar to Nuxt's useCookie)
  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
  };

  const lendoUserId = getCookie('lendo_user_id');

  return {
    id: lendoUserId || undefined,
    returning: undefined,
  };
};

export const initializeTracking = (isProduction = false) => {
  // GTM is already initialized by the script in index.html
  // We just need to push the initial data to the dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    environmentInfo: {
      id: 'lendono',
      environment: isProduction ? 'production' : 'development',
      version: '1.0.0',
      country: 'NO',
      language: 'nb',
    },
    userInfo: getUserInfo(),
  });

  // Set up Usercentrics event listeners
  setupUsercentricsListeners();
  setupTrackingParamsFromUrl();
};

export const trackInternalLead = (leadName: string, eventInfo = {}) => {
    window.dataLayer.push({
        event: 'internalLead',
        eventInfo: {
            leadName: leadName,
            ...eventInfo,
        }
    })
}

export const trackElementInteraction = (elementInfo = {}, eventInfo = {} ): void => {
    window.dataLayer.push({
        event: 'elementInteraction',
        elementInfo,
        eventInfo
    });
}

const isLoaded = () => {
  return typeof window !== 'undefined' && window.dataLayer !== undefined;
};

export const getPageInfo = () => {
  const trackingCookie = useCookie<{ tracking?: number }>('tracking', {
    default: () => ({ tracking: 809 })
  });

  return {
    title: document.getElementsByTagName('title')[0]?.innerHTML,
    location: location.href,
    trackingId: trackingCookie.value?.tracking ?? 809,
    product: 'private',
  };
};

export const dynamicPageView = (pageInfo = {}) => {
  if (isLoaded()) {
    window.dataLayer.push({
      event: 'dynamicPageview',
      pageInfo: { ...getPageInfo(), ...pageInfo },
    });
  }
};

const setupUsercentricsListeners = () => {
  // Listen for consent changes
  window.addEventListener('uc_consent_change', usercentricsConsentChange as EventListener);

  // Listen for banner interactions
  window.addEventListener('uc_ui_interaction', trackCookieBannerInteraction as EventListener);
};

const setupTrackingParamsFromUrl = () => {
  const trackingConfig = {
    max_age: 1800,
    params: [
      'tracking',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'fp',
      'cn',
      'coid',
      'cv',
      'clickid',
      'affiliate',
      'transaction_id',
      'offer_id',
      'pricevariable',
      'at_gd',
      'lsi',
      'refint',
    ],
    rewrite: {
      lsi: 'tracking',
    } as { [key: string]: string },
  }

  // Get URL query parameters using browser API
  const urlParams = new URLSearchParams(window.location.search);
  const query: { [key: string]: string } = {};
  urlParams.forEach((value, key) => {
    query[key] = value;
  });

  // Get existing tracking cookie
  const trackingCookie = useCookie<{ [key: string]: string }>('tracking', {
    maxAge: trackingConfig.max_age,
    path: '/',
  });

  if (Object.keys(query).length > 0) {
    const rewriteParams = Object.keys(trackingConfig.rewrite);

    const filteredParams = Object.keys(query)
      .filter((key) => trackingConfig.params.includes(key))
      .reduce((obj: { [key: string]: string }, key) => {
        const param = rewriteParams.includes(key)
          ? trackingConfig.rewrite[key]
          : key;
        if (param !== undefined) {
          obj[param] = query[key];
        }
        return obj;
      }, {});

    const existingTracking = trackingCookie.value || {};
    const mergedTracking = { ...existingTracking, ...filteredParams };

    if (Object.keys(mergedTracking).length > 0) {
      trackingCookie.value = mergedTracking;
    }
  }
}